import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CancelOutlined from "@mui/icons-material/CancelOutlined";

function formatEta(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString();
  } catch {
    return null;
  }
}

export default function ClippingJobCard({ job, onCancel, lang, fetching = false }) {
  const theme = useTheme();
  const status = job.status;
  const progress = Math.min(100, Math.max(0, Number(status?.progress) || 0));
  const eta = formatEta(job.createMeta?.estimatedCompletionTime);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>
            {fetching
              ? lang?.clippingFetchingTitle || "Fetching your clips"
              : lang?.clippingProcessingTitle || "Creating your clips"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {fetching
              ? lang?.clippingFetchingHint ||
                "Processing is complete. Downloading clip metadata and video URLs…"
              : status?.stepMessage ||
                lang?.clippingProcessingHint ||
                "AI is finding highlights and rendering short-form clips. This usually takes 5–30 minutes."}
          </Typography>

          <Box>
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" fontWeight={600}>
                {progress}%
              </Typography>
              {job.requestId && (
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {String(job.requestId).slice(0, 12)}…
                </Typography>
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
              }}
            />
          </Box>

          {eta && (
            <Typography variant="caption" color="text.secondary">
              {lang?.clippingEta || "Estimated completion"}: {eta}
            </Typography>
          )}

          {job.createMeta?.queuePosition != null && (
            <Typography variant="caption" color="text.secondary">
              {lang?.clippingQueue || "Queue position"}: {job.createMeta.queuePosition}
            </Typography>
          )}

          <Button
            variant="outlined"
            color="inherit"
            startIcon={<CancelOutlined />}
            onClick={onCancel}
            sx={{ alignSelf: "flex-start", borderRadius: 2, fontWeight: 700 }}
          >
            {lang?.clippingCancel || "Cancel job"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
